// "Skąd ta liczba" (owner preview 2026-10-26): what a Pulpit figure is made
// of and which orders stand behind each part. Pure folding of the income
// statement already loaded for the dashboard — the parts always add up to
// the tile, because they are sums of the same monthly lines. Profit at sale
// (decision 2026-10-29-zysk-przy-sprzedazy): the lines are the ZK money of the
// ADMIN-only `forecastVsActual` block, so each order list adds up to its part
// (an incomplete cost with its known part, marked). Purchases are not part of
// the result. Nothing here decides recognition or forecast membership.
import type {
  ForecastVsActualOrder,
  IncomeStatementLine,
  IncomeStatementResponse,
} from '../incomeStatement/incomeStatementApi';
import { monthsPending, type DashboardMonth } from './dashboardModel';
import { exportSheetName, type PivotExportTable } from '../incomeStatement/pivotExport';
import { addLedgerMonth, addPlanPending, emptyPendingScope, isScopePending, type PendingScope } from '../pendingScope';

export type DashboardFigure = 'revenueActual' | 'profitActual' | 'revenueForecast' | 'profitForecast';

export type BreakdownList = 'sold' | 'waitingInPeriod' | 'fixed' | 'none';

export interface BreakdownPart {
  key: string;
  value: number;
  list: BreakdownList;
  /** A cost part holding ZK with only their known cost (marked „*”). */
  incomplete?: boolean;
}

export interface FigureBreakdown {
  parts: BreakdownPart[];
  total: number;
  /** The total holds an incomplete cost (known part, marked). */
  incomplete: boolean;
  /** The list shown before the user picks a part. */
  defaultList: BreakdownList;
}

export interface BreakdownOrderRow {
  id: string;
  displayOrderNumber: string;
  customerName: string;
  status: string;
  revenue: number;
  /** Cost of goods sold; the known part when `costPending`. null = unknown (never 0). */
  cost: number | null;
  margin: number | null;
  /** Only the known part of the cost is counted. */
  costPending: boolean;
  months: string[];
}

export interface BreakdownCategoryRow {
  code: string;
  amount: number;
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
// „*” after the months are summed (one rule for every range, `pendingScope.ts`).
const anyPending = (months: DashboardMonth[], basis: 'actual' | 'forecast') => monthsPending(months, basis);
const lineSum = (months: DashboardMonth[], basis: 'actual' | 'forecast', pick: (line: IncomeStatementLine) => number | null) => (
  sum(months.map((month) => pick(month[basis]) ?? 0))
);

/** Realization covers the elapsed months of the period, like the tiles. */
export function figureMonths(figure: DashboardFigure, periodMonths: DashboardMonth[]): DashboardMonth[] {
  return figure === 'revenueActual' || figure === 'profitActual'
    ? periodMonths.filter((month) => month.elapsed)
    : periodMonths;
}

export function buildFigureBreakdown(
  figure: DashboardFigure,
  periodMonths: DashboardMonth[],
): FigureBreakdown {
  const months = figureMonths(figure, periodMonths);
  const below = (basis: 'actual' | 'forecast') => lineSum(months, basis, (line) => line.depreciation + line.financialCosts);
  const withBelow = (basis: 'actual' | 'forecast', parts: BreakdownPart[]) => {
    const value = below(basis);
    return value ? [...parts, { key: 'belowEbitda', value: -value, list: 'none' as const }] : parts;
  };
  const invoiced = lineSum(months, 'actual', (line) => line.revenue);
  const soldGoods = lineSum(months, 'actual', (line) => line.orderCosts);
  const soldPending = anyPending(months, 'actual');
  const waitingPending = months.some((month) => (month.forecast.planPendingCostOrderCount ?? 0) > 0);

  if (figure === 'revenueActual') {
    return { parts: [{ key: 'invoiced', value: invoiced, list: 'sold' }], total: invoiced, incomplete: false, defaultList: 'sold' };
  }

  if (figure === 'revenueForecast') {
    const total = lineSum(months, 'forecast', (line) => line.revenue);
    return {
      parts: [
        { key: 'invoiced', value: invoiced, list: 'sold' },
        { key: 'awaitingInvoice', value: total - invoiced, list: 'waitingInPeriod' },
      ],
      total,
      incomplete: false,
      defaultList: 'waitingInPeriod',
    };
  }

  if (figure === 'profitActual') {
    // Zysk realizacja = zafakturowane − koszt sprzedanego towaru (the ZK list)
    // − koszty stałe − amortyzacja/koszty finansowe − podatek.
    return {
      parts: withBelow('actual', [
        { key: 'invoiced', value: invoiced, list: 'sold' },
        { key: 'soldGoods', value: -soldGoods, list: 'sold', incomplete: soldPending },
        { key: 'fixedCosts', value: -lineSum(months, 'actual', (line) => line.operatingFixedCosts.total), list: 'fixed' },
      ]).concat([{ key: 'incomeTax', value: -lineSum(months, 'actual', (line) => line.incomeTax), list: 'none' }]),
      total: lineSum(months, 'actual', (line) => line.profit),
      incomplete: soldPending,
      defaultList: 'sold',
    };
  }

  // Forecast = the invoiced part + the orders waiting for their invoice in
  // the period (their planned cost of goods), the same split as the lines.
  const forecastRevenue = lineSum(months, 'forecast', (line) => line.revenue);
  const forecastGoods = lineSum(months, 'forecast', (line) => line.orderCosts);
  return {
    parts: withBelow('forecast', [
      { key: 'invoiced', value: invoiced, list: 'sold' },
      { key: 'soldGoods', value: -soldGoods, list: 'sold', incomplete: soldPending },
      { key: 'awaitingInvoice', value: forecastRevenue - invoiced, list: 'waitingInPeriod' },
      { key: 'awaitingGoods', value: -(forecastGoods - soldGoods), list: 'waitingInPeriod', incomplete: waitingPending },
      { key: 'fixedCosts', value: -lineSum(months, 'forecast', (line) => line.operatingFixedCosts.total), list: 'fixed' },
    ]).concat([{ key: 'incomeTax', value: -lineSum(months, 'forecast', (line) => line.incomeTax), list: 'none' }]),
    total: lineSum(months, 'forecast', (line) => line.profit),
    incomplete: anyPending(months, 'forecast'),
    defaultList: 'waitingInPeriod',
  };
}

/**
 * A ZK shown in a list: some money (a cost catch-up without revenue stays — it
 * is in the tile). An invoice and its withdrawal net to ~0 and are not listed,
 * but every sum („Razem”, a rep's row) still counts every row, so it equals
 * the Rachunek wyników line to the grosz.
 */
export function isListedOrder(row: Pick<BreakdownOrderRow, 'revenue' | 'cost'>): boolean {
  return Math.abs(row.revenue) > 0.5 || Math.abs(row.cost ?? 0) > 0.5;
}

/** The block's ZK rows (one per month) as one row per ZK, all of them; also „Handlowcy” (`salesReps/`). */
export function foldOrders(rows: ForecastVsActualOrder[], basis: 'actual' | 'forecast'): BreakdownOrderRow[] {
  const byId = new Map<string, BreakdownOrderRow>();
  // A ZK's cost is incomplete when it still is after its months in the list are summed.
  const scopes = new Map<string, PendingScope>();
  for (const row of rows) {
    const scope = scopes.get(row.id) ?? emptyPendingScope();
    if (basis === 'actual') addLedgerMonth(scope, row.id, row.pendingDeltas, row.costPending || row.totalCost == null);
    else if (row.costPending || row.totalCost == null) addPlanPending(scope, row.id);
    scopes.set(row.id, scope);
    const current = byId.get(row.id) || {
      id: row.id,
      displayOrderNumber: row.displayOrderNumber,
      customerName: row.customer?.name || '—',
      status: row.status,
      revenue: 0,
      cost: 0,
      margin: 0,
      costPending: false,
      months: [],
    };
    const cost = row.totalCost;
    current.revenue += row.revenue;
    current.cost = current.cost == null || cost == null ? null : current.cost + cost;
    current.margin = current.margin == null || row.margin == null ? null : current.margin + row.margin;
    current.costPending = isScopePending(scopes.get(row.id)!);
    if (row.month && !current.months.includes(row.month)) current.months.push(row.month);
    byId.set(row.id, current);
  }
  return Array.from(byId.values())
    .map((row) => ({ ...row, months: [...row.months].sort() }))
    .sort((a, b) => b.revenue - a.revenue);
}

/**
 * Orders behind a part; null = the block is not available to this reader.
 * The listed ones (`isListedOrder`) unless `all` — sums take `all`.
 */
export function breakdownOrders(
  list: 'sold' | 'waitingInPeriod',
  months: DashboardMonth[],
  statement: IncomeStatementResponse,
  all = false,
): BreakdownOrderRow[] | null {
  const block = statement.forecastVsActual;
  if (!block) return null;
  const monthSet = new Set(months.map((month) => month.month));
  const rows = list === 'sold' ? block.orders.actual : block.orders.forecast;
  const folded = foldOrders(rows.filter((row) => row.month && monthSet.has(row.month)), list === 'sold' ? 'actual' : 'forecast');
  return all ? folded : folded.filter(isListedOrder);
}

export function breakdownCategories(months: DashboardMonth[], basis: 'actual' | 'forecast'): BreakdownCategoryRow[] {
  const totals = new Map<string, number>();
  for (const month of months) {
    for (const [code, amount] of Object.entries(month[basis].operatingFixedCosts.byCategory)) {
      totals.set(code, (totals.get(code) || 0) + amount);
    }
  }
  return Array.from(totals.entries())
    .filter(([, amount]) => Math.abs(amount) > 0.5)
    .map(([code, amount]) => ({ code, amount }))
    .sort((a, b) => b.amount - a.amount);
}

export interface BreakdownOrdersTotal {
  revenue: number;
  /** null when some order's cost is unknown (never 0). */
  cost: number | null;
  margin: number | null;
  /** Some order has only the known part of its cost (after its months are summed). */
  costPending: boolean;
}

/** „Razem” of the order list: the sum of ALL its rows (`breakdownOrders(…, true)`), so it equals the tile's part. */
export function breakdownOrdersTotal(rows: BreakdownOrderRow[]): BreakdownOrdersTotal {
  return rows.reduce<BreakdownOrdersTotal>((total, row) => ({
    revenue: total.revenue + row.revenue,
    cost: total.cost == null || row.cost == null ? null : total.cost + row.cost,
    margin: total.margin == null || row.margin == null ? null : total.margin + row.margin,
    costPending: total.costPending || row.costPending,
  }), { revenue: 0, cost: 0, margin: 0, costPending: false });
}

export interface BreakdownExportLabels {
  title: string;
  /** Sheet name source (cut to a valid Excel name); the title otherwise. */
  sheet?: string;
  info: string[];
  order: string;
  customer: string;
  revenue: string;
  cost: string;
  margin: string;
  marginPercent: string;
  months: string;
  total: string;
  orderLabel: (number: string) => string;
  monthsLabel: (months: string[]) => string;
}

const whole = (value: number | null) => (value == null ? null : Math.round(value));
const onePlace = (margin: number | null, revenue: number) => (
  margin == null || !(revenue > 0) ? null : Math.round((margin / revenue) * 1000) / 10
);

/**
 * The order list of „Skąd ta liczba” as the table of the Excel export used by
 * „Przekrój sprzedaży” (POST /reports/sales-pivot/export): the same rows, the
 * same „Razem” and the same „*” as the screen.
 */
export function buildBreakdownExport(rows: BreakdownOrderRow[], total: BreakdownOrdersTotal, labels: BreakdownExportLabels): PivotExportTable {
  const marked = (pending: boolean, margin: number | null) => (pending && margin != null ? { marked: [3, 4, 5] } : {});
  return {
    title: labels.title,
    sheetName: exportSheetName(labels.sheet ?? labels.title),
    info: labels.info,
    columns: [
      { header: labels.order, format: 'text' },
      { header: labels.customer, format: 'text' },
      { header: labels.revenue, format: 'money' },
      { header: labels.cost, format: 'money' },
      { header: labels.margin, format: 'money' },
      { header: labels.marginPercent, format: 'percent' },
      { header: labels.months, format: 'text' },
    ],
    rows: [
      ...rows.map((row) => ({
        kind: 'order' as const,
        level: 0,
        cells: [labels.orderLabel(row.displayOrderNumber), row.customerName, whole(row.revenue), whole(row.cost), whole(row.margin), onePlace(row.margin, row.revenue), labels.monthsLabel(row.months)],
        ...marked(row.costPending, row.margin),
      })),
      {
        kind: 'total' as const,
        level: 0,
        cells: [labels.total, '', whole(total.revenue), whole(total.cost), whole(total.margin), onePlace(total.margin, total.revenue), ''],
        ...marked(total.costPending, total.margin),
      },
    ],
  };
}
